/**
 * DrillAttachmentPickerModal
 *
 * Coach-only modal for attaching a drill to a conversation message.
 * Two tabs:
 *   "Drill Library" — lists all drills available to the coach that have a
 *     real video (not a placeholder URL). Tapping "Attach" calls onDrillSelected.
 *   "Local File" — prompts the coach to open the native file browser via
 *     onLocalFile(); the existing video-upload flow handles the rest.
 */

import { useState, useEffect } from 'react';
import type { Drill, DrillShotType, ProVideo } from '../types/lesson';
import { getAllDrillsForCoach } from '../services/drills';
import { getAllProVideosForCoach } from '../services/proVideos';

// ── Shot type styling ─────────────────────────────────────────────────────────

const SHOT_TYPE_COLORS: Record<DrillShotType, string> = {
  forehand:  'bg-blue-100 text-blue-700',
  backhand:  'bg-purple-100 text-purple-700',
  serve:     'bg-orange-100 text-orange-700',
  volley:    'bg-green-100 text-green-700',
  return:    'bg-cyan-100 text-cyan-700',
  movement:  'bg-amber-100 text-amber-700',
  general:   'bg-gray-100 text-gray-600',
};

const SHOT_TYPES: DrillShotType[] = [
  'forehand', 'backhand', 'serve', 'volley', 'return', 'movement', 'general',
];

function isPlaceholder(url: string) {
  return !url || url.startsWith('https://placeholder.drill/');
}

// ── Component ─────────────────────────────────────────────────────────────────

interface DrillAttachmentPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  coachId: string;
  /** Coach selected a drill — caller sends it as a drill_attachment message */
  onDrillSelected: (drill: Drill) => void;
  /** Coach selected a pro video — caller sends it as a pro_video_attachment message */
  onProVideoSelected: (video: ProVideo) => void;
  /** Coach chose "Local File" — caller opens the native file browser */
  onLocalFile: () => void;
}

type Tab = 'drills' | 'pro-library' | 'file';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function DrillAttachmentPickerModal({
  isOpen,
  onClose,
  coachId,
  onDrillSelected,
  onProVideoSelected,
  onLocalFile,
}: DrillAttachmentPickerModalProps) {
  const [tab, setTab] = useState<Tab>('drills');

  // Drill library state
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shotFilter, setShotFilter] = useState<DrillShotType | 'all'>('all');
  const [previewing, setPreviewing] = useState<string | null>(null);

  // Pro library state
  const [stockProVideos, setStockProVideos] = useState<ProVideo[]>([]);
  const [coachProVideos, setCoachProVideos] = useState<ProVideo[]>([]);
  const [proLoading, setProLoading] = useState(false);
  const [proLoaded, setProLoaded] = useState(false);
  const [proPreviewing, setProPreviewing] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setTab('drills');
    setShotFilter('all');
    setPreviewing(null);
    setProPreviewing(null);
    if (!loaded) {
      setLoading(true);
      getAllDrillsForCoach(coachId)
        .then((all) => setDrills(all.filter((d) => !isPlaceholder(d.videoUrl))))
        .catch(() => {})
        .finally(() => { setLoading(false); setLoaded(true); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load pro videos lazily when that tab is first opened
  useEffect(() => {
    if (tab !== 'pro-library' || proLoaded) return;
    setProLoading(true);
    getAllProVideosForCoach(coachId)
      .then(({ stock, coach }) => {
        setStockProVideos(stock.filter((v) => v.videoUrl && !v.videoUrl.startsWith('https://placeholder.')));
        setCoachProVideos(coach.filter((v) => v.videoUrl && !v.videoUrl.startsWith('https://placeholder.')));
      })
      .catch(() => {})
      .finally(() => { setProLoading(false); setProLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, proLoaded]);

  // Fresh load on next open after close
  useEffect(() => {
    if (!isOpen) { setLoaded(false); setProLoaded(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  const filtered = shotFilter === 'all' ? drills : drills.filter((d) => d.shotType === shotFilter);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" data-testid="drill-attachment-picker">
      <div
        className="rounded-xl w-full max-w-lg mx-4 shadow-xl flex flex-col max-h-[85vh]"
        style={{ background: '#152238', border: '1px solid #1E3048' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#F1F5F9]">Attach to Message</h3>
            <p className="text-xs text-[#8899AA] mt-0.5">Share a drill, pro video, or attach a file</p>
          </div>
          <button onClick={onClose} className="text-[#8899AA] hover:text-[#F1F5F9] ml-4 flex-shrink-0" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex px-5 flex-shrink-0" style={{ borderBottom: '1px solid #1E3048' }}>
          {([['drills', 'Drill Library'], ['pro-library', 'Pro Library'], ['file', 'Local File']] as [Tab, string][]).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`drill-picker-tab-${t}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                tab === t
                  ? 'border-[#1A6B8A] text-[#1A6B8A]'
                  : 'border-transparent text-[#8899AA] hover:text-[#F1F5F9]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">

          {/* Drill Library tab */}
          {tab === 'drills' && (
            <div className="space-y-3">
              {/* Shot type filter chips */}
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setShotFilter('all')}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                    shotFilter === 'all'
                      ? 'bg-[#1A6B8A] text-white border-[#1A6B8A]'
                      : 'border-[#2A3F5C] text-[#8899AA] hover:border-[#4A6080] hover:text-[#F1F5F9]'
                  }`}
                >
                  All
                </button>
                {SHOT_TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setShotFilter(t)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                      shotFilter === t
                        ? 'bg-[#1A6B8A] text-white border-[#1A6B8A]'
                        : 'border-[#2A3F5C] text-[#8899AA] hover:border-[#4A6080] hover:text-[#F1F5F9]'
                    }`}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* List */}
              {loading && (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 border-2 border-[#8899AA] border-t-[#F1F5F9] rounded-full animate-spin" />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <p className="text-xs text-[#8899AA] text-center py-8">
                  {drills.length === 0 ? 'No drills with videos found.' : 'No drills match this filter.'}
                </p>
              )}
              {!loading && filtered.map((drill) => {
                const isPreviewing = previewing === drill.id;
                return (
                  <div
                    key={drill.id}
                    data-testid="drill-picker-item"
                    className="rounded-lg overflow-hidden border border-[#2A3F5C]"
                    style={{ background: '#1E3048' }}
                  >
                    <div className="flex items-start gap-2 px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${SHOT_TYPE_COLORS[drill.shotType]}`}>
                            {drill.shotType}
                          </span>
                          {drill.durationSeconds && (
                            <span className="text-[10px] text-[#8899AA]">{formatDuration(drill.durationSeconds)}</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-[#F1F5F9] [overflow-wrap:anywhere]">{drill.title}</p>
                        {drill.description && (
                          <p className="text-xs text-[#8899AA] [overflow-wrap:anywhere] mt-0.5">{drill.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                        <button
                          onClick={() => setPreviewing(isPreviewing ? null : drill.id)}
                          className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                            isPreviewing ? 'text-[#1A6B8A] bg-[#1A6B8A]/20' : 'text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#2A3F5C]'
                          }`}
                          title={isPreviewing ? 'Close preview' : 'Preview'}
                        >
                          {isPreviewing
                            ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                            : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          }
                        </button>
                        <button
                          onClick={() => { onDrillSelected(drill); onClose(); }}
                          data-testid="drill-picker-attach-btn"
                          className="px-2.5 py-1 text-xs rounded border bg-[#1A6B8A] text-white border-[#1A6B8A] hover:bg-[#0F4A63] transition-colors"
                        >
                          Attach
                        </button>
                      </div>
                    </div>
                    {isPreviewing && (
                      <div className="border-t border-[#1E3048] bg-black">
                        <video src={drill.videoUrl} controls className="w-full max-h-48 object-contain" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pro Library tab */}
          {tab === 'pro-library' && (
            <div className="space-y-3">
              {proLoading && (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 border-2 border-[#8899AA] border-t-[#F1F5F9] rounded-full animate-spin" />
                </div>
              )}
              {!proLoading && stockProVideos.length === 0 && coachProVideos.length === 0 && (
                <p className="text-xs text-[#8899AA] text-center py-8">
                  No pro videos available yet. Admin can add videos to the DB Pro Library, and you can add your own under Account &rarr; Pro Library.
                </p>
              )}
              {!proLoading && (
                <>
                  {/* DB Pro Library section */}
                  {stockProVideos.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-[#8899AA] px-0.5 mb-2">DB Pro Library</p>
                      <div className="space-y-2">
                        {stockProVideos.map((video) => {
                          const url = video.webOptimizedVideoUrl ?? video.videoUrl;
                          const isPreviewing = proPreviewing === video.id;
                          return (
                            <div
                              key={video.id}
                              data-testid="pro-picker-item"
                              className="rounded-lg overflow-hidden border border-[#2A3F5C]"
                              style={{ background: '#1E3048' }}
                            >
                              <div className="flex items-start gap-2 px-3 py-2.5">
                                {video.thumbnailUrl && (
                                  <img src={video.thumbnailUrl} alt="" className="w-10 h-7 rounded object-cover flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  {video.playerName && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full mr-1" style={{ background: '#064E3B', color: '#6EE7B7', border: '1px solid #065F46' }}>
                                      {video.playerName}
                                    </span>
                                  )}
                                  <p className="text-sm font-medium text-[#F1F5F9] [overflow-wrap:anywhere] mt-0.5">{video.title}</p>
                                  {video.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {video.tags.map((tag) => (
                                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: '#1A3A5C', color: '#7EB4D4', border: '1px solid #2A5070' }}>{tag}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                                  <button
                                    onClick={() => setProPreviewing(isPreviewing ? null : video.id)}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${isPreviewing ? 'text-[#1A6B8A] bg-[#1A6B8A]/20' : 'text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#2A3F5C]'}`}
                                    title={isPreviewing ? 'Close preview' : 'Preview'}
                                  >
                                    {isPreviewing
                                      ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                                      : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                    }
                                  </button>
                                  <button
                                    onClick={() => { onProVideoSelected({ ...video, videoUrl: url }); onClose(); }}
                                    data-testid="pro-picker-attach-btn"
                                    className="px-2.5 py-1 text-xs rounded border bg-[#1A6B8A] text-white border-[#1A6B8A] hover:bg-[#0F4A63] transition-colors"
                                  >
                                    Attach
                                  </button>
                                </div>
                              </div>
                              {isPreviewing && (
                                <div className="border-t border-[#1E3048] bg-black">
                                  <video src={url} controls className="w-full max-h-48 object-contain" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* My Pro Library section */}
                  {coachProVideos.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-[#8899AA] px-0.5 mb-2 mt-3">My Pro Library</p>
                      <div className="space-y-2">
                        {coachProVideos.map((video) => {
                          const url = video.webOptimizedVideoUrl ?? video.videoUrl;
                          const isPreviewing = proPreviewing === video.id;
                          return (
                            <div
                              key={video.id}
                              data-testid="pro-picker-item"
                              className="rounded-lg overflow-hidden border border-[#2A3F5C]"
                              style={{ background: '#1E3048' }}
                            >
                              <div className="flex items-start gap-2 px-3 py-2.5">
                                {video.thumbnailUrl && (
                                  <img src={video.thumbnailUrl} alt="" className="w-10 h-7 rounded object-cover flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  {video.playerName && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full mr-1" style={{ background: '#064E3B', color: '#6EE7B7', border: '1px solid #065F46' }}>
                                      {video.playerName}
                                    </span>
                                  )}
                                  <p className="text-sm font-medium text-[#F1F5F9] [overflow-wrap:anywhere] mt-0.5">{video.title}</p>
                                  {video.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {video.tags.map((tag) => (
                                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: '#1A3A5C', color: '#7EB4D4', border: '1px solid #2A5070' }}>{tag}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                                  <button
                                    onClick={() => setProPreviewing(isPreviewing ? null : video.id)}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${isPreviewing ? 'text-[#1A6B8A] bg-[#1A6B8A]/20' : 'text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#2A3F5C]'}`}
                                    title={isPreviewing ? 'Close preview' : 'Preview'}
                                  >
                                    {isPreviewing
                                      ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                                      : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                    }
                                  </button>
                                  <button
                                    onClick={() => { onProVideoSelected({ ...video, videoUrl: url }); onClose(); }}
                                    data-testid="pro-picker-attach-btn"
                                    className="px-2.5 py-1 text-xs rounded border bg-[#1A6B8A] text-white border-[#1A6B8A] hover:bg-[#0F4A63] transition-colors"
                                  >
                                    Attach
                                  </button>
                                </div>
                              </div>
                              {isPreviewing && (
                                <div className="border-t border-[#1E3048] bg-black">
                                  <video src={url} controls className="w-full max-h-48 object-contain" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Local File tab */}
          {tab === 'file' && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#1E3048' }}>
                <svg className="w-7 h-7 text-[#8899AA]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.8l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[#F1F5F9] mb-1">Attach a video file</p>
                <p className="text-xs text-[#8899AA]">Opens your device's file browser. Max 200 MB.</p>
              </div>
              <button
                onClick={() => { onLocalFile(); onClose(); }}
                className="px-5 py-2.5 bg-[#1A6B8A] text-white rounded-lg hover:bg-[#0F4A63] text-sm font-medium transition-colors"
              >
                Open File Browser
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 pt-3 flex-shrink-0" style={{ borderTop: '1px solid #1E3048' }}>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg text-sm text-[#8899AA] hover:text-[#F1F5F9] transition-colors"
            style={{ border: '1px solid #1E3048' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
